import React from "react";
import { Collapse, Paper, Typography } from "@mui/material";
import SuggestedItemCarousel from "./SuggestedItemCarousel";
import { generatePackSuggestionsFromTableData } from "../utils/packSuggestionUtils";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import LoadingItemSuggestion from "./LoadingItemSuggestion";
import SuggestionSettings from "./SuggestionSettings";
import { HelpCircle, Settings, X } from "lucide-react";
import { getBorder, getMutedText, HEADING_FONT } from "../constants/colors";
import { useMediaQuery } from "react-responsive";

const ItemSuggestion = ({
  tableData,
  weightsInGrams,
  packIsLoading,
  itemCategoryList,
  darkMode,
}) => {
  const isMobile = useMediaQuery({ maxWidth: 898 });
  const [suggestedItems, setSuggestedItems] = React.useState([]);
  const [suggestedItemsAreLoading, setSuggestedItemsAreLoading] =
    React.useState(false);
  const [timeOfLastSettingsUpdate, setTimeOfLastSettingsUpdate] =
    React.useState(Date.now());
  const [suggestionSettingsOpen, setSuggestionSettingsOpen] =
    React.useState(false);

  React.useEffect(() => {
    (async () => {
      setSuggestedItemsAreLoading(true);
      const newSuggestedItems = await generatePackSuggestionsFromTableData(
        tableData
      );
      setSuggestedItems(newSuggestedItems);
      setSuggestedItemsAreLoading(false);
    })();
  }, [itemCategoryList, weightsInGrams, tableData, timeOfLastSettingsUpdate]);

  return packIsLoading || suggestedItemsAreLoading ? (
    <Paper
      elevation={0}
      sx={{
        width: isMobile ? "700px" : "380px",
        minHeight: isMobile ? "400px" : "272px",
        borderRadius: "16px",
        border: `1px solid ${getBorder(darkMode)}`,
        backgroundImage: "none",
        backgroundColor: darkMode ? "#262626" : "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <LoadingItemSuggestion />
    </Paper>
  ) : suggestedItems.length ? (
    <Paper
      elevation={0}
      sx={{
        width: isMobile ? "700px" : "380px",
        height: isMobile ? "400px" : "272px",
        borderRadius: "16px",
        border: `1px solid ${getBorder(darkMode)}`,
        backgroundImage: "none",
        backgroundColor: darkMode ? "#262626" : "#FFFFFF",
      }}
    >
      <SuggestedItemCarousel
        suggestedItems={suggestedItems}
        weightsInGrams={weightsInGrams}
        setTimeOfLastSettingsUpdate={setTimeOfLastSettingsUpdate}
        suggestionSettingsOpen={suggestionSettingsOpen}
        setSuggestionSettingsOpen={setSuggestionSettingsOpen}
        darkMode={darkMode}
      />
    </Paper>
  ) : (
    <Paper
      elevation={0}
      sx={{
        width: isMobile ? "700px" : "380px",
        minHeight: isMobile ? "400px" : "220px",
        marginTop: "20px",
        marginLeft: "10px",
        borderRadius: "16px",
        border: `1px solid ${getBorder(darkMode)}`,
        backgroundImage: "none",
        backgroundColor: darkMode ? "#262626" : "#FFFFFF",
      }}
    >
      <Tooltip
        title={
          <>
            Suggestions are based on the item types of the items in your pack
            and your gears weight. If you have no suggestions, you may need to
            reconfigure your item types or you might just have the lightest item
            available!
            <p
              style={{
                fontStyle: "italic",
                textDecoration: "underline",
                paddingTop: "0px",
                paddingBottom: "-5px",
                color: "#BBB",
              }}
            >
              Note
            </p>
            <p style={{ marginTop: "-10px" }}>
              For the best suggestions, try to use the provided item types in
              the dropdown as opposed to entering your own, when possible.
            </p>
            <p>
              You should always do your due diligence on suggested items. The
              item suggested to you might be a variant that is not your size or
              does not serve your needs appropriately.
            </p>
          </>
        }
      >
        <IconButton
          sx={{
            height: isMobile ? "40px" : "36px",
            width: isMobile ? "40px" : "36px",
            marginTop: isMobile ? "20px" : "0px",
            marginLeft: isMobile ? "10px" : "0px",
          }}
        >
          <HelpCircle size={isMobile ? 28 : 20} />
        </IconButton>
      </Tooltip>
      <IconButton
        sx={{
          height: "40px",
          width: "40px",
          marginTop: isMobile ? "20px" : "0px",
          marginLeft: isMobile ? "600px" : "300px",
        }}
        onClick={() => {
          setSuggestionSettingsOpen(!suggestionSettingsOpen);
        }}
      >
        <Settings size={isMobile ? 28 : 20} />
      </IconButton>
      <Collapse in={suggestionSettingsOpen} orientation="horizontal">
        <Paper
          elevation={0}
          sx={{
            zIndex: "2",
            width: "375px",
            marginLeft: "2px",
            height: "220px",
            position: "absolute",
            borderRadius: "12px",
            border: `1px solid ${getBorder(darkMode)}`,
            backgroundImage: "none",
            backgroundColor: darkMode ? "#262626" : "#FFFFFF",
            boxShadow: darkMode
              ? "0 12px 28px rgba(0,0,0,0.5)"
              : "0 12px 28px rgba(0,0,0,0.15)",
          }}
        >
          <IconButton
            sx={{ right: "0px", top: "0px", position: "absolute" }}
            onClick={() => {
              setSuggestionSettingsOpen(false);
              setTimeOfLastSettingsUpdate(Date.now());
            }}
          >
            <X size={18} />
          </IconButton>
          <SuggestionSettings
            setTimeOfLastSettingsUpdate={setTimeOfLastSettingsUpdate}
          />
        </Paper>
      </Collapse>
      <Box
        sx={{
          width: isMobile ? "700px" : "380px",
          textAlign: "center",
        }}
      >
        <Stack
          sx={{
            justifyContent: "center",
            height: isMobile ? "220px" : "150px",
            lineHeight: "150px",
          }}
        >
          <Typography
            sx={{
              fontSize: isMobile ? "20px" : "15px",
              fontFamily: HEADING_FONT,
              color: getMutedText(darkMode),
            }}
          >
            No suggestions for this Pack
          </Typography>
        </Stack>
      </Box>
    </Paper>
  );
};

export default ItemSuggestion;
